import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppPermission } from './../../models/app-permission';
import { AuthUser } from './../../models/auth-user';

import { VirtualizationSettings } from '@progress/kendo-angular-dropdowns';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { App, DismissedSiteDataPreviewConfigurationMessage, EmailNotificationCadence, Project, Site, UserEmailRequest, UserPhoneRequest } from '../../models';
import { User } from '../../models/user';
import { AUTH_MESSAGES, SNACKBAR_CONSTANTS } from '../../shared/constants';
import { ClientErrorStatusCodes, HttpErrorLocationMessages } from '../../shared/enums/http-status-codes-enum';
import { AppsDataService, UsersDataService } from '../data-services';

@Injectable({
    providedIn: 'root'
})
export class UsersLogicService {
    private _currentUsers: User[] = [];
    private _platformUsers: User[] = [];
    private _authUser: AuthUser | null = null;
    private loadUsersByApp: Subject<boolean> = new Subject<boolean>();
    private currentUserReady: Subject<boolean> = new Subject<boolean>();
    private loadedPlatformUsers: Subject<boolean> = new Subject<boolean>();
    private _usersLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _userDismissedGuardRail: Subject<boolean> = new Subject<boolean>();
    private _appAdminLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _loadAppAdmins = new Subject<boolean>();

    loadUsersByApp$ = this.loadUsersByApp.asObservable();
    currentUserReady$ = this.currentUserReady.asObservable();
    usersLoading$ = this._usersLoading.asObservable();
    loadedPlatformUsers$ = this.loadedPlatformUsers.asObservable();
    _userDismissedGuardRail$ = this._userDismissedGuardRail.asObservable();
    appAdminLoading$ = this._appAdminLoading.asObservable();
    loadAppAdmins$ = this._loadAppAdmins.asObservable();

    authUser: BehaviorSubject<AuthUser | null> = new BehaviorSubject<AuthUser | null>(null);
    users: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
    user: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
    appUser: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
    allUsers: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
    appUsers: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
    appAdmins: BehaviorSubject<User[]> = new BehaviorSubject<User[]>(null);

    currentUser: User | null = null;
    currentAppUser: User | null = null;
    isSaving = false;
    siteUsers: User[] = [];
    virtualizationSettings: VirtualizationSettings = { itemHeight: 60, pageSize: 50 };

    get currentUsers(): User[] {
        return this._currentUsers;
    }

    get platformUsers(): User[] {
        return this._platformUsers;
    }

    constructor(
        private _usersDataService: UsersDataService,
        private _appsDataService: AppsDataService,
        private _snackBar: MatSnackBar,
    ) { }

    init(): void {
        this.getUserFromSessionStorage();
        this.getAppUserFromSessionStorage();
        this.getAuthUserFromSessionStorage();
    }

    reloadUser(): void {
        if (this.currentUser) {
            this.getUser(this.currentUser.id);
        }
    }

    setAuthUser(authUser: AuthUser): void {
        this._authUser = authUser;
        this.authUser.next(authUser);
        sessionStorage.setItem("authUser", JSON.stringify(authUser));
    }

    setUser(user: User): void {
        this.setCurrentUser(user);
        sessionStorage.setItem("user", JSON.stringify(user));
    }

    setAppUser(user: User): void {
        this.setCurrentAppUser(user);
        sessionStorage.setItem("appUser", JSON.stringify(user));
    }

    setUsers(users: any): void {
        sessionStorage.setItem('users', JSON.stringify(users));
        this.siteUsers = users;
        this.sortUsers(this.siteUsers);
        this.users.next(users);
    }

    disconnectUser(): void {
        this.setCurrentUser(null);
        this.setCurrentAppUser(null);
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("appUser");
    }

    setCurrentAppUser(user: User | null): void {
        this.appUser.next(user);
        this.currentAppUser = user;
    }

    setCurrentUser(user: User | null): void {
        this.user.next(user);
        this.currentUser = user;
        this.currentUserReady.next(true);
    }

    setTermsAccepted(value: boolean): void {
        if (this.currentUser) {
            this.currentUser.acceptedTOS = value;
        }
    }

    addUpdateUser(user: User): void {
        this.addUsers(this._currentUsers, user);

        this.addUsers(this._platformUsers, user);

        this.allUsers.next(this._currentUsers);
    }

    addUsers(users: User[], user: User): void {
        if (users.find(u => u.id === user.id)) {
            const index = users.findIndex(u => u.id === user.id);
            users[index] = user;
        }
        else {
            users.push(user);
        }
    }

    appsAndProjectsByUserId(userId: string): Observable<any> {
        const appsAndProjectsByUserId = new Subject<any>();

        const user: User = { id: userId };

        this._usersDataService.appsAndProjectsByUserId(user)
            .subscribe((response: User) => {
                this.addUpdateUser(response);
                appsAndProjectsByUserId.next(response);
            });

        return appsAndProjectsByUserId.asObservable();
    }

    addUserToApp(app: App, project: Project, user: User): void {
        this.isSaving = true;
        this._usersDataService.addUserToApp(user)
            .subscribe({
                next: (response: HttpErrorResponse | User) => {
                    if (response instanceof HttpErrorResponse) {
                        return;
                    }

                    this._snackBar.open(`User added to the ${app.name} application and the ${project.name} ${app.aliases.project.singular}.`, `Close`, { duration: 10000 });
                    this.addUpdateUser(response);
                },
                error: (error: HttpErrorResponse) => {
                    if (error.status === ClientErrorStatusCodes.Conflict) {
                        this._snackBar.open(`This user has already been added to the ${app.name} application.`, `Close`, { duration: 10000 });
                        return;
                    }
                    if (error.status === ClientErrorStatusCodes.NotFound) {
                        this._snackBar.open(`This user no longer exists in the application. Please create new user.`, `Close`, { duration: 10000 });
                        return;
                    }

                    this._snackBar.open(`Error adding user to ${app.name}.`, `Close`, { duration: 5000 });
                }
            })
            .add(() => {
                this.loadUsersByApp.next(true);
                this.isSaving = false;
            });
    }

    createPlatformUserAndAddToApp(app: App, user: User, project: Project): void {
        this._usersDataService.createPlatformUserAndAddToApp(user)
            .subscribe({
                next: (user: User) => {
                    this.addUpdateUser(user);
                    this._snackBar.open(`User created and added user to the ${app.name} application and the ${project.name} ${app.aliases.project.singular}.`, `Close`, { duration: 5000 });
                    this.isSaving = false;
                },
                error: (error: HttpErrorResponse) => {
                    if (error.status !== ClientErrorStatusCodes.Conflict) {
                        this._snackBar.open(`Error creating user.`, `Close`, { duration: 5000 });
                        return;
                    }
                    if (error.error === HttpErrorLocationMessages.Phone) {
                        this._snackBar.open(`A user with this phone number already exists. Please add the existing user.`, `Close`, { duration: 10000 });
                        return;
                    }
                    this._snackBar.open(`A user with this email already exists. Please add the existing user.`, `Close`, { duration: 10000 });
                    return;
                }
            })
            .add(() => {
                this.loadUsersByApp.next(true);
                this.isSaving = false;
            });
    }

    createPlatformUser(user: User): Observable<any> {
        return this._usersDataService.createPlatformUser(user).pipe(tap((createdUser: User) => {
            this.addUpdateUser(createdUser);
        }));
    }

    getPlatformUsersIfTheyHaveNotBeenFetched(): void {
        if (this._platformUsers?.length) {
            this.loadedPlatformUsers.next(true);
            return;
        }

        this.getPlatformUsers();
    }

    getPlatformUsers(): void {
        this._usersLoading.next(true);

        this._usersDataService.getPlatformUsers()
            .subscribe({
                next: (users: User[]) => {
                    this._platformUsers = users;
                },
                error: () => {
                    this._platformUsers = [];
                }
            })
            .add(() => {
                this._usersLoading.next(false);
                this.loadedPlatformUsers.next(true);
            });
    }

    getUser(id: string): void {
        this._usersDataService.getUser(id)
            .subscribe((response: User) => {
                this.setCurrentUser(response);
                sessionStorage.setItem("user", JSON.stringify(response));
            });
    }

    loadCurrentUser(id: string): Observable<User> {
        return this._usersDataService.getUser(id).pipe(
            switchMap((user: User) => {
                this.setCurrentUser(user);
                sessionStorage.setItem("user", JSON.stringify(user));
                return of(user);
            })
        );
    }

    getUsersByApp(app: App): void {
        this._usersDataService.getUsersByApp(app)
            .subscribe({
                next: (response: User[]) => {
                    this._currentUsers = response;
                    this.setUsers(response);
                },
                error: () => {
                    this._currentUsers = [];
                    this.setUsers([]);
                }
            })
            .add(() => {
                this._usersLoading.next(false);
            });
    }

    loadUsersByApplicationAndProject(app: App, project: Project): void {
        if (!project?.id) {
            return;
        }

        this._usersDataService.loadUsersByApplicationAndProject(app, project)
            .subscribe({
                next: (response: User[]) => {
                    this._currentUsers = response;
                    this.setUsers(response);
                },
                error: () => {
                    this._currentUsers = [];
                    this.setUsers([]);
                }
            })
            .add(() => {
                this._usersLoading.next(false);
            });
    }

    getAppUser(app: App, user: User): void {
        this._usersDataService.getAppUser(app, user)
            .subscribe((response: User) => {
                this.setCurrentAppUser(response);
                sessionStorage.setItem("appUser", JSON.stringify(response));
            });
    }

    removeUserFromApp(user: User): void {
        this._usersDataService.removeUserFromApp(user)
            .subscribe({
                next: () => {
                    this._snackBar.open(`User removed.`, "Close", { duration: 5000 });
                },
                error: () => {
                    this._snackBar.open(`Error removing user from app`, `Close`, { duration: 5000 });
                }
            })
            .add(() => {
                user.isSaving = false;
                this.loadUsersByApp.next(true);
            });
    }

    updateUserSitesAccess(user: User, app: App, project: Project): void {
        this._usersDataService.updateUserSitesAccess(user, app, project)
            .subscribe({
                next: () => {
                    this._snackBar.open(`User's ${app.aliases.site.plural.toLocaleLowerCase()} access saved.`, "Close", { duration: 5000 });
                },
                error: () => {
                    this._snackBar.open(`Error saving user's ${app.aliases.site.singular.toLocaleLowerCase()} access`, `Close`, { duration: 5000 });
                }
            })
            .add(() => {
                user.isSaving = false;
                this.loadUsersByApp.next(true);
            });
    }

    getAppAdminsByAppId(app: App): void {
        this._appAdminLoading.next(true);

        this._usersDataService.getAppAdminsByAppId(app)
            .subscribe({
                next: (response: User[]) => {
                    response ? this.appAdmins.next(response) : this.appAdmins.next([]);
                },
                error: () => {
                    this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('app admin', 'loading', true), SNACKBAR_CONSTANTS.CLOSE, { duration: 5000 });
                }
            })
            .add(() => {
                this._appAdminLoading.next(false);
            });
    }

    setAsProjectAdmin(app: App, project: Project, user: User): void {
        this._usersDataService.updateProjectAdminStatus(app, project, user)
            .subscribe({
                next: (response: User) => {
                    this._snackBar.open(`${app.aliases.project.singular} Admin role ${user.isProjectAdmin ? 'added' : 'removed'} for '${project.name}''.`, 'Close', { duration: 5000 });
                    this.addUpdateUser(response);
                },
                error: () => {
                    this._snackBar.open(`Error setting ${app.aliases.project.singular.toLocaleLowerCase()} admin`, `Close`, { duration: 5000 });
                    user.isProjectAdmin = !user.isProjectAdmin;
                }
            })
            .add(() => {
                user.setProjectAdminIsLoading = false;
                this.loadUsersByApp.next(true);
            });
    }

    setAsAppAdmin(app: App, user: User, project: Project): void {
        this._usersDataService.updateAppAdminStatus(app, user)
            .subscribe({
                next: (response: User) => {
                    this.addUpdateUser(response);
                    this._snackBar.open(`App Admin role ${user.isAppAdmin ? 'added' : 'removed'} for '${project.name}''.`, 'Close', { duration: 5000 });
                },
                error: () => {
                    user.isAppAdmin = !user.isAppAdmin;
                    this._snackBar.open(`Error setting application admin`, `Close`, { duration: 5000 });
                }
            })
            .add(() => {
                user.setAppAdminIsLoading = false;
                this.loadUsersByApp.next(true);
            });
    }

    updatePlatformUser(user: User): Observable<any> {
        const updatePlatformUser = new Subject<any>();

        this._usersDataService.updatePlatformUser(user)
            .subscribe((response: User) => {
                updatePlatformUser.next(response);
            });

        return updatePlatformUser.asObservable();
    }

    isAccountApprover(user: User): Observable<any> {
        const isAccountApprover = new Subject<any>();

        this._usersDataService.isAccountApprover(user)
            .subscribe((response: User) => {
                isAccountApprover.next(response);
            });

        return isAccountApprover.asObservable();
    }

    changeActive(user: User): Observable<any> {
        const changeActive = new Subject<any>();

        this._usersDataService.changeActive(user)
            .subscribe((response) => {
                changeActive.next(response);
            });

        return changeActive.asObservable();
    }

    editPlatformUserProperties(user: User): Observable<any> {
        return this._usersDataService.editPlatformUserProperties(user).pipe(tap((updatedUser: User) => {
            this.addUpdateUser(updatedUser);
            this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_SUCCESS('user', 'saved'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
        }), catchError((error: Error) => {
            this._snackBar.open(SNACKBAR_CONSTANTS.ITEM_ACTION_ERROR('user', 'saved'), SNACKBAR_CONSTANTS.CLOSE, { duration: 3000 });
            throw error;
        }));
    }

    changeUserRole(app: App, user: User): Observable<any> {
        const changeUserRole = new Subject<any>();

        this._usersDataService.updateUserRole(app, user)
            .subscribe((response: User) => {
                user.isLoading = false;
                this.addUpdateUser(response);
                this.loadUsersByApp.next(true);
                this._snackBar.open(`User role updated.`, `Close`, { duration: 2000 });
            });

        return changeUserRole.asObservable();
    }

    loadUserApps(): Observable<App[]> {
        const loadApps = new Subject<App[]>();

        this._appsDataService.loadUserApps(this.currentUser!)
            .subscribe((response: App[]) => {
                loadApps.next(response);
            });

        return loadApps.asObservable();
    }

    updateUserAccountSettings(user: User): void {
        this._usersDataService.updateUserAccountSettings(user)
            .subscribe({
                next: (user: User) => {
                    this.setUser(user);
                    this._snackBar.open(`User information updated.`, "Close", { duration: 5000 });
                    this.isSaving = false;
                },
                error: (error: Error) => {
                    this._snackBar.open(error.message, "Close");
                }
            })
            .add(() => {
                this.isSaving = false;
            });
    }

    addUserEmailNotificationCadenceToPlatformUser(user: User, emailNotificationCadence: EmailNotificationCadence): void {
        this.isSaving = true;
        this._usersDataService.addUserEmailNotificationCadenceToPlatformUser(user, emailNotificationCadence)
            .subscribe({
                next: (user: User) => {
                    this.setUser(user);
                    this.isSaving = false;
                    this._snackBar.open(`Notification preferences successfully saved.`, 'Close', { duration: 2000, });
                },
                error: (error: Error) => {
                    this._snackBar.open(error.message, "Close");
                }
            })
            .add(() => {
                this.isSaving = false;
            });
    }

    updateUserProjectRole(user: User, project: Project, roleId: string): void {
        const userProject = user.projects?.find((proj: Project) => proj.projectId === project.id);

        if (userProject) {
            userProject.roleId = roleId;
        }
    }

    addProjectToUser(user: User, project: Project): void {
        const userProject = user.projects?.find((proj: Project) => proj.projectId === project.id);

        if (!userProject) {
            const projectToAdd = new Project();
            projectToAdd.projectId = project.id;
            projectToAdd.isProjectAdmin = false;
            if (user?.projects) {
                user.projects.push(projectToAdd);
            }
        }
    }

    setProjectUser(user: User, project: Project, app: App): void {
        this.isSaving = true;

        user.projId = project.id;
        user.appId = app.id;

        this._usersDataService.setProjectUserAccess(user)
            .subscribe({
                next: (response: User) => {
                    this._snackBar.open(`User ${user.isProjectUser ? 'added to ' : 'removed from '} ${project.name}.`, 'Close', { duration: 5000 });
                    this.addUpdateUser(response);
                    this.loadUsersByApp.next(true);
                },
                error: () => {
                    user.isProjectUser = !user.isProjectUser;
                }
            })
            .add(() => {
                this.isSaving = false;
                user.setProjectUserIsLoading = false;
                this.loadUsersByApp.next(true);
            });
    }

    doesUserHaveAccessToAllSites(user: User): boolean {
        return (
            user.isAppAdmin ||
            !user.sites ||
            user.sites.length === 0
        );
    }

    getProjectAndSiteUsers(projectId: string, currentSite: Site): User[] {
        const filteredUsers: User[] = this.filterUsersByProjectAndSite(this.siteUsers, projectId, currentSite);
        const index = filteredUsers.findIndex((user: User) => user.id === this.currentUser?.id);

        if (index !== -1) {
            const elementToMove = filteredUsers.splice(index, 1)[0];
            filteredUsers.unshift(elementToMove);
            return filteredUsers;
        }

        if (!this.currentAppUser) {
            return filteredUsers;
        }

        this.currentAppUser.name = `${this.currentUser?.firstName} ${this.currentUser?.lastName}`;
        filteredUsers.unshift(this.currentAppUser);
        return filteredUsers;
    }

    getProjectUsers(currentProject: Project): User[] {
        const filteredUsers: User[] = this.filterUsersByProject(this.currentUsers, currentProject);

        if (!this.currentUser) {
            return filteredUsers;
        }

        const index = filteredUsers.findIndex((user: User) => user.id === this.currentUser?.id);

        if (index !== -1) {
            const elementToMove = filteredUsers.splice(index, 1)[0];
            filteredUsers.unshift(elementToMove);
            return filteredUsers;
        }

        if (!this.currentAppUser) {
            return filteredUsers;
        }

        this.currentAppUser.name = `${this.currentUser?.firstName} ${this.currentUser?.lastName}`;
        filteredUsers.unshift(this.currentAppUser);
        return filteredUsers;
    }

    isAuthUserAppAdmin(appId: string | null): boolean {
        if (this._authUser?.isPlatformAdmin) {
            return true;
        }

        const appPermission = this.getAppPermissionForAuthUser(appId);
        return appPermission?.isAppAdmin ?? false;
    }

    isAuthUserProjectAdmin(appId: string | null, projectId: string | null): boolean {
        if (!projectId) {
            return false;
        }

        if (this.isAuthUserAppAdmin(appId)) {
            return true;
        }

        const appPermission = this.getAppPermissionForAuthUser(appId);
        const userProject = appPermission?.userProjects?.find(proj => proj.projectId === projectId);
        return userProject?.isProjectAdmin ?? false;
    }

    isAuthUserAnyProjectAdmin(appId: string): boolean {
        if (this.isAuthUserAppAdmin(appId)) {
            return true;
        }

        const appPermission = this.getAppPermissionForAuthUser(appId);
        return appPermission?.userProjects?.some(proj => proj.isProjectAdmin) ?? false;
    }

    isAuthUserAnyAdmin(): boolean {
        if (!this._authUser) {
            return false;
        }

        if (this._authUser?.isPlatformAdmin) {
            return true;
        }

        if (this._authUser?.appPermissions?.some(appPerm => appPerm.isAppAdmin)) {
            return true;
        }

        if (this._authUser?.appPermissions?.some(appPerm => appPerm.userProjects?.some(proj => proj.isProjectAdmin))) {
            return true;
        }

        return false;
    }

    isAuthUserPlatformAdmin(): boolean {
        return this._authUser?.isPlatformAdmin ?? false;
    }

    doesAuthUserHaveAccessToProject(appId: string | null, projectId: string | null,): boolean {
        if (this.isAuthUserPlatformAdmin() || this.isAuthUserAppAdmin(appId)) {
            return true;
        }

        if (!projectId) {
            return false;
        }

        const appPermission = this.getAppPermissionForAuthUser(appId);
        return appPermission?.userProjects?.some(proj => proj.projectId === projectId) ?? false;
    }

    doesAuthUserHaveAccessToSummaryProject(appId: string | null, summaryProject: Project): boolean {
        if (this.isAuthUserPlatformAdmin() || this.isAuthUserAppAdmin(appId)) {
            return true;
        }

        // Users are never assigned to a summary project; membership in any project it reads from grants access.
        const sourceProjectIds = new Set<string>(summaryProject.consumesFromProjectIds ?? []);
        const appPermission = this.getAppPermissionForAuthUser(appId);
        return appPermission?.userProjects?.some(proj => !!proj.projectId && sourceProjectIds.has(proj.projectId)) ?? false;
    }

    private getAppPermissionForAuthUser(appId: string | null): AppPermission | undefined {
        if (!this._authUser?.appPermissions || !appId) {
            return undefined;
        }

        return this._authUser.appPermissions.find(appPerm => appPerm.id === appId);
    }

    private filterUsersByProject(users: User[], currentProject: Project): User[] {
        const projectIds = new Set<string>(currentProject.isSummaryProject ? currentProject.consumesFromProjectIds ?? [] : [currentProject.id ?? '']);

        return users.filter((user: User) => {
            if (user.isAppAdmin) {
                return true;
            }

            if (!user.projects || user.projects.length === 0) {
                return false;
            }

            const userProject: Project | undefined = user.projects.find(userProject => !!userProject.projectId && projectIds.has(userProject.projectId));
            if (!userProject) {
                return false;
            }

            return true;
        });
    }

    private filterUsersByProjectAndSite(users: User[], projectId: string, currentSite: Site): User[] {
        return users?.filter((user: User) => {
            if (user.isAppAdmin) {
                return true;
            }

            if (!user.projects || user.projects.length === 0) {
                return false;
            }

            const userProject: Project | undefined = user.projects.find(userProject => projectId === userProject.projectId);
            if (!userProject) {
                return false;
            }

            if (userProject.isProjectAdmin) {
                return true;
            }

            if (user.sites?.includes(currentSite?.id)) {
                return true;
            }

            if (!user.sites) {
                return true;
            }

            return false;
        });
    }

    private getAuthUserFromSessionStorage(): void {
        const authUserString = sessionStorage.getItem("authUser");
        if (authUserString) {
            const authUser = JSON.parse(authUserString);
            this.setAuthUser(authUser);
        }
    }

    private getUserFromSessionStorage(): void {
        const userString = sessionStorage.getItem("user");
        if (userString) {
            const user = JSON.parse(userString);
            this.setUser(user);
        }
    }

    private getAppUserFromSessionStorage(): void {
        const appUserString = sessionStorage.getItem("appUser");
        if (appUserString) {
            const appUser = JSON.parse(appUserString);
            this.setAppUser(appUser);
        }
    }

    sortUsers(users: User[] | null): User[] {
        if (!users) {
            return [];
        }
        return users.sort((firstUser: User, secondUser: User) => (firstUser?.firstName ?? '') < (secondUser?.firstName ?? '') ? -1 : 1);
    }

    dismissMapSiteDataPreviewConfigurationDialog(currentUser: User, dismissedSiteDataPreviewConfigurationMessage: DismissedSiteDataPreviewConfigurationMessage, app: App): void {
        this._usersDataService.dismissMapSiteDataPreviewConfigurationMessage(currentUser, dismissedSiteDataPreviewConfigurationMessage, app)
            .subscribe({
                next: (updatedUser: User) => {
                    this.addUpdateUser(updatedUser);
                    this.setAppUser(updatedUser);
                    this._userDismissedGuardRail.next(true);
                },
                error: () => {
                    this._snackBar.open(`Error dismissing map preview configuration message`, 'Close', { duration: 3000 });
                }
            });
    }

    userEmailExist(email: string): Observable<boolean> {
        const userEmailRequest: UserEmailRequest = { email: email };
        return this._usersDataService.userEmailExist(userEmailRequest)
            .pipe(
                catchError(() => {
                    this._snackBar.open(AUTH_MESSAGES.EMAIL_VALIDATION_ERROR, AUTH_MESSAGES.CLOSE_BUTTON, { duration: 3000 });
                    return of(false);
                })
            );
    }

    userPhoneNumberExist(cellPhone: string): Observable<boolean> {
        const userPhoneRequest: UserPhoneRequest = { cellphone: cellPhone };
        return this._usersDataService.userPhoneNumberExist(userPhoneRequest)
            .pipe(
                catchError(() => {
                    this._snackBar.open(AUTH_MESSAGES.PHONE_VALIDATION_ERROR, AUTH_MESSAGES.CLOSE_BUTTON, { duration: 3000 });
                    return of(false);
                })
            );
    }

    removeAppAdmin(app: App, user: User): void {
        this._usersDataService.removeAppAdmin(app, user)
            .subscribe({
                next: () => {
                    this._snackBar.open(`User removed.`, `Close`, { duration: 5000 });
                    this._loadAppAdmins.next(true);
                }
            });
    }

    addAppAdmin(app: App, user: User): void {
        this._usersDataService.addAppAdmin(app, user)
            .subscribe({
                next: () => {
                    this._snackBar.open(`User added.`, "Close", { duration: 5000 });
                    this._loadAppAdmins.next(true);
                    this.isSaving = false;
                }
            });
    }
}
